import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { 
  CreditCard, User, Globe, FileText, ChevronRight, CheckCircle, Clock, Euro,
  ChevronDown, CalendarCheck, Printer, Download, AlertCircle, Phone
} from 'lucide-react';

const CATEGORY_LABELS = {
  documentos: 'services.cat_documentos',
  vistos: 'services.cat_vistos',
  certidoes: 'services.cat_certidoes',
  notarial: 'services.cat_notarial',
};

const CATEGORY_COLORS = {
  documentos: 'border-l-blue-500',
  vistos: 'border-l-green-500',
  certidoes: 'border-l-purple-500',
  notarial: 'border-l-orange-500',
};

const CATEGORY_ICONS = {
  documentos: CreditCard,
  vistos: Globe,
  certidoes: FileText,
  notarial: FileText,
};

export default function ConsularServices() {
  const { t } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    api.get('/services').then(r => setServices(r.data || [])).finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(services.map(s => s.category))];
  const filteredServices = selectedCategory 
    ? services.filter(s => s.category === selectedCategory)
    : services;

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getCategoryLabel = (cat) => {
    const key = CATEGORY_LABELS[cat];
    return key ? t(key) : cat;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <Link to="/" className="hover:text-white">{t('nav.home')}</Link>
            <ChevronRight size={12} />
            <span className="text-white">{t('services.title')}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{t('services.title')}</h1>
          <p className="text-gray-400 max-w-2xl">
            {t('services.subtitle')}
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/agendamento" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#CC0000] text-white font-medium rounded-md hover:bg-[#A30000] transition-colors text-sm">
              <CalendarCheck size={16} /> {t('services.schedule_appointment')}
            </Link>
            <Link to="/cartao-consular" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 text-white font-medium rounded-md hover:bg-white/20 transition-colors text-sm">
              <CreditCard size={16} /> {t('services.consular_card_online')}
            </Link>
            <Link to="/salvo-conduto" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 text-white font-medium rounded-md hover:bg-white/20 transition-colors text-sm">
              <Globe size={16} /> Salvo-Conduto
            </Link>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 py-3 overflow-x-auto">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                !selectedCategory ? 'bg-[#CC0000] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t('services.all_services')}
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat ? 'bg-[#CC0000] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {getCategoryLabel(cat)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredServices.map(svc => {
              const isExpanded = expandedId === svc.id;
              const CategoryIcon = CATEGORY_ICONS[svc.category] || FileText;
              
              return (
                <div 
                  key={svc.id} 
                  data-testid={`service-detail-${svc.id}`}
                  className={`bg-white rounded-xl border-l-4 ${CATEGORY_COLORS[svc.category] || 'border-l-gray-300'} shadow-sm overflow-hidden`}
                >
                  {/* Service Header */}
                  <button
                    onClick={() => toggleExpand(svc.id)}
                    className="w-full p-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600">
                        <CategoryIcon size={22} />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{svc.name}</h3>
                        <p className="text-sm text-gray-500">{svc.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <div className="text-xl font-bold text-[#CC0000]">€{svc.fee?.toFixed(2)}</div>
                        <div className="text-xs text-gray-400 flex items-center gap-1 justify-end">
                          <Clock size={11} /> {svc.processing_time}
                        </div>
                      </div>
                      <ChevronDown size={20} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50 p-5 space-y-5">
                      {/* Price and Time - Mobile */}
                      <div className="flex items-center gap-4 sm:hidden">
                        <div className="flex-1 bg-white p-3 rounded-lg text-center border border-gray-200">
                          <div className="text-xl font-bold text-[#CC0000]">€{svc.fee?.toFixed(2)}</div>
                          <div className="text-xs text-gray-400">{t('services.fee')}</div>
                        </div>
                        <div className="flex-1 bg-white p-3 rounded-lg text-center border border-gray-200">
                          <div className="text-sm font-bold text-gray-700">{svc.processing_time}</div>
                          <div className="text-xs text-gray-400">{t('services.processing')}</div>
                        </div>
                      </div>

                      {/* Requirements Checklist */}
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                          <CheckCircle size={16} className="text-green-500" />
                          {t('services.required_docs_checklist')}
                        </h4>
                        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                          {svc.requirements?.map((req, i) => (
                            <label key={i} className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer">
                              <input type="checkbox" className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#CC0000] focus:ring-[#CC0000]" />
                              <span className="text-sm text-gray-700">{req}</span>
                            </label>
                          ))}
                        </div>
                        <button className="mt-2 text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                          <Printer size={12} /> {t('services.print_checklist')}
                        </button>
                      </div>

                      {/* Important Notes */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h5 className="font-semibold text-amber-800 text-sm mb-1">{t('services.important_info')}</h5>
                            <ul className="text-xs text-amber-700 space-y-1">
                              <li>- {t('services.info_original_copy')}</li>
                              <li>- {t('services.info_appointment')}</li>
                              <li>- {t('services.info_payment')}</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-3">
                        <Link 
                          to={`/agendamento?service=${svc.id}`}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#CC0000] text-white font-medium rounded-md hover:bg-[#A30000] transition-colors text-sm"
                        >
                          <CalendarCheck size={16} /> {t('services.schedule_appointment')}
                        </Link>
                        <Link 
                          to={`/pagamentos?service=${svc.category}`}
                          className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors text-sm"
                        >
                          <Euro size={16} /> {t('services.pay_fee_online')}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Contact Box */}
        <div className="mt-10 bg-[#0A0A0A] text-white rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-bold text-lg mb-3 text-[#FFD700]">{t('services.need_help')}</h3>
              <p className="text-sm text-gray-300 mb-4">
                {t('services.need_help_desc')}
              </p>
              <div className="space-y-2">
                <a href="tel:+34913096901" className="flex items-center gap-2 text-sm text-gray-300 hover:text-white">
                  <Phone size={14} className="text-[#FFD700]" /> +34 913 096 901
                </a>
                <a href="mailto:info@embajadangola.es" className="flex items-center gap-2 text-sm text-gray-300 hover:text-white">
                  <FileText size={14} className="text-[#FFD700]" /> info@embajadangola.es
                </a>
              </div>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-3 text-[#FFD700]">{t('services.opening_hours')}</h3>
              <div className="text-sm text-gray-300 space-y-1">
                <div className="flex justify-between">
                  <span>{t('services.weekdays')}</span>
                  <span className="font-medium text-white">09:00 - 15:00</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('services.weekends')}</span>
                  <span className="text-red-400">{t('services.closed')}</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                * {t('services.appointment_recommended')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
